import { InsurancePlan } from 'src/services/interfaces/insurance';

interface CignaCardProps {
    side?: 'front' | 'back';
    plan?: InsurancePlan;
    firstName?: string;
    lastName?: string;
    memberId?: string | number;
    groupId?: string | number
    rxGroupId?: string;
}

export const CignaCard = ({ side = 'front', plan, firstName, lastName, memberId, groupId, rxGroupId }: CignaCardProps) => {
    const memberData = {
        firstName: firstName || '{FirstName}',
        lastName: lastName || '{Lastname}',
        memberId: memberId || '{0000000000000}',
        groupNumber: groupId || '{TRIAD}',
        inNetworkDeductible: '{$0,000}',
        inNetworkMaxOutOfPocket: '{$00,000}',
        outOfNetworkDeductible: '{$0,000}',
        outOfNetworkMaxOutOfPocket: '{$00,000}',
        rxCopays: rxGroupId || '{Rxcopays}',
    };

    if (side === 'back') {
        return (
            <div className="relative aspect-[672/411] max-w-[672px] origin-center scale-60 md:scale-100 border border-[#e0e0e0] rounded-2xl system-font">
                {/* Providers Section */}
                <div className="pt-4 px-4">
                    <div className="text-base text-black">
                        <div>
                            <div className="leading-none font-bold text-black">Providers</div>
                            <span className="leading-none font-bold">For prescription claims/inquiries call: </span>
                            <span className="leading-none font-normal">(888) 589-3340</span>
                        </div>
                        <div>
                            <span className="leading-none font-bold">For medical pre-certification call: </span>
                            <span className="leading-none font-normal">(866) 494-4872</span>
                        </div>
                        <div>
                            <span className="leading-none font-bold">For eligibility, coverage, and claim status:</span>
                        </div>
                        <div>
                            <span className="leading-none font-normal">Go to portal.securusbenefits.com/providers or call </span>
                            <span className="leading-none font-normal">(203) 208-9898</span>
                        </div>
                    </div>
                </div>

                {/* Members Section */}
                <div className="px-4 mt-2">
                    <div className="text-base text-black">
                        <div className="leading-none font-bold text-black">Members</div>
                        <div>
                            <span className="font-bold">For plan, can, billing, or claims questions, call: </span>
                            <span className="font-normal">(877) 420-0785</span>
                        </div>
                        <div>
                            <span className="font-bold">For prescription questions, call: </span>
                            <span className="font-normal">(888) 589-3340</span>
                        </div>
                    </div>
                </div>

                {/* Submit claims to Section */}
                <div className="px-4 mt-2">
                    <div className="leading-none font-bold text-black">Submit claims to:</div>
                    <div className="flex gap-8">
                        {/* Medical Column */}
                        <div className="flex-1">
                            <div className="text-base text-black">
                                <div className="leading-none font-bold">
                                    <span className='font-bold'>Medical: </span>
                                    <span className="font-normal">Cigna Health</span>
                                </div>
                                <div className="font-normal"><span className='leading-none font-bold'>Payer ID: </span>62308</div>
                                <div className="font-normal"><span className='leading-none font-bold'>PO Box: </span>188061</div>
                                <div className="font-normal"><span className='leading-none font-bold'>City, State, Zip: </span>Chattanooga, TN, 37422</div>
                            </div>
                        </div>

                        {/* Pharmacy Column */}
                        <div className="flex-1">
                            <div className="text-base text-black">
                                <div className="font-normal">
                                    <span className='leading-none font-bold'>Pharmacy:</span>
                                    <span className='leading-none font-normal'>DisclosedRx</span>
                                </div>
                                <div className="font-normal"><span className='leading-none font-bold'>Rx BIN: </span>021601</div>
                                <div className="font-normal"><span className='leading-none font-bold'>Rx PCN: </span>DRX</div>
                                <div className="font-normal"><span className='leading-none font-bold'>Rx Group #: </span>{memberData.groupNumber}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Disclaimer Section */}
                <div className="px-4 pb-4 mt-4">
                    <p className="text-xs text-black leading-relaxed">
                        This card is not a guarantee of coverage. Coverage may be subject to limitations and conditions. Pre-certification is required for some services. Failure to obtain pre-certification may result in a reduction of benefits. Member may be eligible for financial assistance. Benefits are not insured by Cigna Healthcare or affiliates.
                    </p>
                </div>
            </div>
        );
    }

    const detailsCoverage = (plan?.details as any)?.coverage

    return (
        <div className="relative aspect-[672/434] max-w-[672px] origin-center scale-60 md:scale-100 border border-[#e0e0e0] rounded-2xl">
            {/* Top Section */}
            <div className="flex justify-between items-start pb-4 border-b border-[#d0d0d0] mb-4 pt-4 px-4">
                <div>
                    <h2 className="text-3xl font-bold text-black mb-2">
                        {memberData.firstName} {memberData.lastName}
                    </h2>
                    <div className="text-base text-black">
                        <span className="font-normal">Member ID</span>{' '}
                        <span className="font-bold">{memberData.memberId}</span>
                    </div>
                    <div className="text-base text-black">
                        <span className="font-normal">Group #</span>{' '}
                        <span className="font-bold">{memberData.groupNumber}</span>
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-base text-black font-semibold mb-2">Network Provided by:</div>
                    <img src="/images/cigna.png" alt="Cigna" className="h-12" />
                </div>
            </div>

            {/* Middle Section */}
            <div className="flex gap-8 pb-4 border-b border-[#d0d0d0] mb-4 px-4">
                {/* In-Network Column */}
                <div className="flex-1">
                    <h3 className="text-lg font-bold text-black">In-Network</h3>
                    <div className="text-base text-black">
                        <span className="font-normal">Deductible</span>{' '}
                        <span className="font-normal">${detailsCoverage?.["Deductible (Individual) - In Network"]}</span>
                    </div>
                    <div className="text-base text-black">
                        <span className="font-normal">Max Out-of-Pocket</span>{' '}
                        <span className="font-normal">${detailsCoverage?.["Out of Pocket Maximum (Individual) - In Network"]}</span>
                    </div>
                </div>

                {/* Out-of-Network Column */}
                <div className="flex-1">
                    <h3 className="text-lg font-bold text-black">Out-of-Network</h3>
                    <div className="text-base text-black">
                        <span className="font-normal">Deductible</span>{' '}
                        <span className="font-normal">${detailsCoverage?.["Deductible (individual) - Out of Network"]}</span>
                    </div>
                    <div className="text-base text-black">
                        <span className="font-normal">Max Out-of-Pocket</span>{' '}
                        <span className="font-normal">${detailsCoverage?.["Out of Pocket Maximum (Individual) - Out of Network"]}</span>
                    </div>
                </div>
            </div>

            {/* Bottom Section */}
            <div className="flex gap-4 px-4 pb-4">
                {/* Prescription Questions Column */}
                <div className="flex-1">
                    <div className="w-full bg-black text-white py-2 mb-3 text-center font-medium transition-colors">
                        Have a prescription question?
                    </div>
                    <div className="text-base text-black">
                        <div>
                            <span className="font-bold">Call PBM:</span>{' '}
                            <span className="font-normal">(888) 589-3340</span>
                        </div>
                        <div className="mb-3">
                            <span className="font-bold">Rx Costs</span>{' '}
                            <span className="font-normal">{memberData.rxCopays}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <img src="/images/disclosedrx.png" alt="DisclosedRx" className="h-12" />
                        </div>
                    </div>
                </div>

                {/* Medical Care Questions Column */}
                <div className="flex-1">
                    <div className="w-full bg-black text-white py-2 mb-3 text-center font-medium transition-colors">
                        Have a medical care question?
                    </div>
                    <div className="text-base text-black">
                        <div>
                            <span className="font-bold">Call OneHealth:</span>{' '}
                            <span className="font-normal">(877) 420-0785</span>
                        </div>
                        <div>
                            <span className="font-bold">Member Portal:</span>{' '}
                            <span className="font-normal">portal.onehealthplus.com</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};